import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebounce } from '@/hooks/use-debounce';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  ActivityLogsQuery,
  ActivityLogsResponse,
  type ActivityLogsResponse as ActivityLogsResponseType,
  PassportEventListDto,
} from '@tunodo/ticketing-shared';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/admin/passport/logs')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden acceder a esta sección.');
      throw redirect({ to: '/' });
    }
  },
  validateSearch: ActivityLogsQuery,
  component: PassportLogsPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPageRange(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const delta = 2;
  const range: number[] = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i);
  }

  const result: (number | 'ellipsis')[] = [1];
  if ((range[0] ?? 2) > 2) result.push('ellipsis');
  result.push(...range);
  if ((range[range.length - 1] ?? total - 1) < total - 1) result.push('ellipsis');
  result.push(total);
  return result;
}

function TypeBadge({ type }: { type: 'TALK' | 'SECONDARY' | 'SPONSOR' }) {
  if (type === 'TALK') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
        Ponente
      </span>
    );
  }
  if (type === 'SPONSOR') {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800">
        Sponsor
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
      Secundaria
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function PassportLogsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [emailInput, setEmailInput] = useState(search.userEmail ?? '');
  const debouncedEmail = useDebounce(emailInput, 300);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void navigate({
      search: (prev) => ({ ...prev, userEmail: debouncedEmail.trim() || undefined, page: 1 }),
      replace: true,
    });
  }, [debouncedEmail, navigate]);

  const { data: passportEvents } = useQuery({
    queryKey: ['passport-admin', 'events'],
    queryFn: () => apiFetch('/api/passport-admin/events', { schema: PassportEventListDto }),
    staleTime: 60_000,
  });

  const showEventSelector = passportEvents && passportEvents.length > 1;

  const { data, isLoading } = useQuery<ActivityLogsResponseType>({
    queryKey: [
      'passport-admin',
      'logs',
      {
        type: search.type,
        eventId: search.eventId,
        userEmail: search.userEmail,
        page: search.page,
        pageSize: search.pageSize,
      },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search.type) params.set('type', search.type);
      if (search.eventId) params.set('eventId', search.eventId);
      if (search.userEmail) params.set('userEmail', search.userEmail);
      params.set('page', String(search.page));
      params.set('pageSize', String(search.pageSize));
      return apiFetch(`/api/passport-admin/logs?${params}`, { schema: ActivityLogsResponse });
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / search.pageSize)) : 1;

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Logs de actividades del pasaporte</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Registro de asistentes que escanearon QRs de ponentes y sponsors
        </p>
      </div>

      {/* Event selector — visible solo si hay múltiples eventos passport */}
      {showEventSelector && (
        <div className="max-w-xs">
          <Select
            value={search.eventId ?? 'all'}
            onValueChange={(value) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  eventId: value === 'all' ? undefined : value,
                  page: 1,
                }),
                replace: true,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos los eventos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los eventos</SelectItem>
              {passportEvents.map((ev) => (
                <SelectItem key={ev.id} value={ev.id}>
                  {ev.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex-1 min-w-0">
          <Input
            placeholder="Buscar por email del asistente..."
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
        </div>

        <Select
          value={search.type ?? 'all'}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                type: value === 'all' ? undefined : (value as 'TALK' | 'SECONDARY' | 'SPONSOR'),
                page: 1,
              }),
              replace: true,
            })
          }
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="TALK">Ponente</SelectItem>
            <SelectItem value="SPONSOR">Sponsor</SelectItem>
            <SelectItem value="SECONDARY">Secundaria</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            <TableRow>
              <TableHead>Fecha/hora</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Actividad</TableHead>
              <TableHead>Ponente/Sponsor</TableHead>
              <TableHead>Email asistente</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-36" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                  No hay registros con los filtros seleccionados.
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              data?.items.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {new Date(entry.scannedAt).toLocaleString('es-CO')}
                  </TableCell>
                  <TableCell>
                    <TypeBadge type={entry.activity.type} />
                  </TableCell>
                  <TableCell className="text-sm">{entry.activity.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.activity.speakerName ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.user.email}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
          <p className="text-sm text-muted-foreground order-2 sm:order-1">
            {data.total} registro{data.total !== 1 ? 's' : ''}
            {' · '}página {search.page} de {totalPages}
          </p>
          <Pagination className="w-auto order-1 sm:order-2">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  className={cn(
                    'cursor-pointer',
                    search.page <= 1 && 'pointer-events-none opacity-50',
                  )}
                  onClick={() =>
                    search.page > 1 &&
                    void navigate({
                      search: (prev) => ({ ...prev, page: search.page - 1 }),
                      replace: true,
                    })
                  }
                />
              </PaginationItem>

              {getPageRange(search.page, totalPages).map((item, i) =>
                item === 'ellipsis' ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ellipsis separators have no identity
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationLink
                      className="cursor-pointer"
                      isActive={item === search.page}
                      onClick={() =>
                        void navigate({
                          search: (prev) => ({ ...prev, page: item }),
                          replace: true,
                        })
                      }
                    >
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  className={cn(
                    'cursor-pointer',
                    search.page >= totalPages && 'pointer-events-none opacity-50',
                  )}
                  onClick={() =>
                    search.page < totalPages &&
                    void navigate({
                      search: (prev) => ({ ...prev, page: search.page + 1 }),
                      replace: true,
                    })
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </main>
  );
}
